<template>
    <div class="auth-wrapper auth-v1">
        <div class="row w-100">
            <div class="col-md-4 col-lg-4 offset-md-4 offset-lg-4">

                <Overlay :show="showLoading">
                    <!-- Login v1 -->
                    <b-card class="mb-0">
                        <b-link class="brand-logo">
                            <vuexy-logo />

                            <img :src="logo" style="max-width: 160px">
                        </b-link>

                        <validation-observer
                            ref="loginForm"
                            #default="{invalid}"
                        >
                            <b-form
                                class="auth-login-form mt-2"
                                @submit.prevent="authenticate"
                            >
                                <!-- email -->
                                <b-form-group
                                    label-for="email"
                                    :label="$t('Page(Login):Component(Label):email')"
                                >
                                    <validation-provider
                                        #default="{ errors }"
                                        name="Email"
                                        rules="required|email"
                                    >
                                        <b-form-input
                                            id="email"
                                            v-model="userEmail"
                                            name="login-email"
                                            :state="errors.length > 0 ? false:null"
                                            placeholder="john@example.com"
                                            autofocus
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>

                                <!-- forgot password -->
                                <b-form-group>
                                    <div class="d-flex justify-content-between">
                                        <label for="login-password">{{ $t('Page(Login):Component(Label):password') }}</label>
                                        <b-link :to="{name:'auth-forgot-password'}">
                                            <small>{{ $t('Page(Login):Component(Link):forgot_password') }}</small>
                                        </b-link>
                                    </div>
                                    <validation-provider
                                        #default="{ errors }"
                                        name="Password"
                                        vid="password"
                                        rules="required"
                                    >
                                        <b-input-group
                                            class="input-group-merge"
                                            :class="errors.length > 0 ? 'is-invalid':null"
                                        >
                                            <b-form-input
                                                id="login-password"
                                                v-model="password"
                                                :state="errors.length > 0 ? false:null"
                                                class="form-control-merge"
                                                :type="passwordFieldType"
                                                name="login-password"
                                                :placeholder="$t('Page(Login):Component(Placeholder):password')"
                                            />
                                            <b-input-group-append is-text>
                                                <feather-icon
                                                    class="cursor-pointer"
                                                    :icon="passwordToggleIcon"
                                                    @click="togglePasswordVisibility"
                                                />
                                            </b-input-group-append>
                                        </b-input-group>
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-form-group>

                                <b-alert
                                    v-height-fade.appear
                                    variant="danger"
                                    :show="showIncorrectPassword"
                                    class="mb-1"
                                >
                                    <div class="alert-body">
                                        <feather-icon
                                            icon="InfoIcon"
                                            class="mr-50"
                                        />
                                        Incorrect username or password.
                                    </div>
                                </b-alert>

                                <!-- submit button -->
                                <b-button
                                    variant="primary"
                                    type="submit"
                                    block
                                    :disabled="invalid"
                                >
                                    {{ $t('Page(Login):Component(Button):sign_in') }}
                                </b-button>
                            </b-form>
                        </validation-observer>
                    </b-card>
                </Overlay>
                <div class="text-center mt-1 d-flex justify-content-between">
                    <PrivacyPolicyLink/>
                    <GoogleApiUserDataPolicyLink/>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
/* eslint-disable global-require */
import {ValidationProvider, ValidationObserver} from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import Overlay from "@core/components/common/Overlay.vue";
import {
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BCard,
    BImg,
    BForm,
    BButton,
    BAlert,
    VBTooltip,
    BFormInvalidFeedback,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import useJwt from '@/auth/jwt/useJwt'
import {required, email} from '@validations'
import {togglePasswordVisibility} from '@core/mixins/ui/forms'
import { heightFade } from '@core/directives/animations'
import PrivacyPolicyLink from "@core/components/common/PrivacyPolicyLink.vue";
import GoogleApiUserDataPolicyLink from "@core/components/common/GoogleApiUserDataPolicyLink.vue";
import { $themeConfig } from '@themeConfig'
import {setMfaState} from "@/auth/mfa";

export default {
    directives: {
        'b-tooltip': VBTooltip,
        'height-fade': heightFade,
    },
    components: {
        BLink, BFormGroup, BFormInput, Overlay, PrivacyPolicyLink, BInputGroupAppend, BInputGroup, BFormCheckbox,
        BCardText, BCardTitle, BImg, BForm, BCard, BButton, BAlert, VuexyLogo, ValidationProvider, ValidationObserver,
        vSelect, BFormInvalidFeedback, GoogleApiUserDataPolicyLink,
    },
    mixins: [togglePasswordVisibility],
    data() {
        return {
            status: '',
            password: '',
            userEmail: '',

            showLoading: false,
            // validation rules
            required,
            email,
            logo: $themeConfig.app.brandingLogo,
            backendResponse: {},
            showIncorrectPassword: false,
        }
    },
    computed: {
        passwordToggleIcon() {
            return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
        }
    },
    methods: {
        selectOrganization(organizations) {
            if (organizations.length === 0) {
                this.$swal({
                    icon: 'warning',
                    title: this.$t('Page(Login):Component(Warning):no-organization-message-label'),
                    text: this.$t('Page(Login):Component(Warning):no-organization-message'),
                    customClass: {
                        confirmButton: 'btn btn-primary',
                    },
                })
                return;
            }

            this.$store.commit('setOrganizations', organizations);

            this.$router.push({
                name: 'select-organization'
            });
        },

        mfaAuthentication(methods) {
            setMfaState({
                methods,
                selectedMethod: null,
                challenges: {},
            });

            if (methods.length === 1) {
                return this.$router.push({
                    name: `mfa-${methods[0].type}`,
                });
            }

            this.$router.push({
                name: 'mfa-method-select'
            });
        },

        authenticate() {
            this.$refs.loginForm.validate().then(success => {
                if (success) {
                    this.showLoading = true;
                    useJwt.login({
                        username: this.userEmail,
                        password: this.password,
                    })
                    .then(response => {
                        this.showIncorrectPassword = false;
                        this.backendResponse = response.data;

                        useJwt.setToken(this.backendResponse.token)
                        useJwt.setRefreshToken(this.backendResponse.refresh_token)

                        if (response.data.mfa) {
                            return this.mfaAuthentication(response.data.methods || []);
                        }

                        this.selectOrganization(response.data.organizations);
                    })
                    .catch(error => {
                        this.showIncorrectPassword = true;
                        if (error.response) {
                            this.$refs.loginForm.setErrors(error.response.data.message)
                        }
                    })
                    .finally(() => {
                        this.showLoading = false;
                    })
                }
            })
        },
    },
}
</script>
<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
